import { useDebouncedCallback } from 'use-debounce'
import AsyncSelect from 'react-select/async'
import PropTypes from 'prop-types'
import React, { useCallback, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import useApi from '../hooks/useApi'

function LocationSearch(props) {
  const { disabled, error, onChange, placeholder, value } = props

  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const [lookupFailed, setLookupFailed] = useState(false)
  // Shown while the picked place's details load; then the value prop again.
  const [pickedLabel, setPickedLabel] = useState(null)
  // Only the latest pick may update the field; an older lookup that finishes late is ignored.
  const latestPick = useRef(0)

  const { locationAutocomplete, locationPlaceDetails } = useApi()

  const loadOptions = useDebouncedCallback(
    async input => {
      if (input?.length < 2) {
        return []
      }
      setIsLoading(true)
      const { data } = await locationAutocomplete({ input })
      setIsLoading(false)
      if (data?.error) {
        return []
      }
      const options = data?.map(prediction => ({
        value: prediction.place_id,
        label: prediction.description,
      }))
      return options ?? []
    },
    500,
    { leading: true, trailing: true },
  )

  const onSelect = useCallback(
    async newValue => {
      const pick = ++latestPick.current
      setLookupFailed(false)
      if (!newValue) {
        setPickedLabel(null)
        setIsLoading(false)
        onChange?.(null)
        return
      }
      setPickedLabel(newValue.label)
      setIsLoading(true)
      const { data } = await locationPlaceDetails({
        placeId: newValue.value,
      })
      if (pick !== latestPick.current) return
      if (data?.geometry?.location) {
        onChange?.({ ...data, description: newValue.label })
      } else {
        setLookupFailed(true)
      }
      setPickedLabel(null)
      setIsLoading(false)
    },
    [locationPlaceDetails, onChange],
  )

  const shownValue = pickedLabel ?? value
  // The parent's message is about the previous pick, so it waits while a new one loads.
  const message = lookupFailed
    ? t('users.edit.location_details_error')
    : !pickedLabel && error

  return (
    <>
      <AsyncSelect
        backspaceRemovesValue={false}
        blurInputOnSelect
        isClearable
        isDisabled={disabled}
        isLoading={isLoading}
        isSearchable
        loadOptions={loadOptions}
        onChange={onSelect}
        placeholder={placeholder}
        value={shownValue ? { value: shownValue, label: shownValue } : null}
      />
      {!!message && (
        <div className="text-danger small mt-1" role="alert">
          {message}
        </div>
      )}
    </>
  )
}

export default LocationSearch

LocationSearch.defaultProps = {
  disabled: false,
  error: null,
  onChange: () => {},
  placeholder: '',
  value: '',
}

LocationSearch.propTypes = {
  disabled: PropTypes.bool,
  error: PropTypes.string,
  onChange: PropTypes.func,
  placeholder: PropTypes.string,
  value: PropTypes.string,
}
